import { appendCustomerExclusions } from '@/features/customers/utils/customer-filter-params';
import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import { normalizePro20PageResponse } from '@/shared/api/normalize-pro20-page';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type { Pro20ApiResponse } from '@/shared/types/api-response.types';
import type { PaginatedResponse } from '@/shared/types/api.types';

import type {
  AddBulkMessageCustomersParams,
  BulkMessage,
  BulkMessageCodeParams,
  BulkMessageMultimedia,
  BulkMessageQueueStatusDto,
  CreateBulkMessagePayload,
  GetBulkMessagesByBranchParams,
  PageBulkMessageEvent,
  RetrieveBulkMessageEventsParams,
  SelectBulkMessageCustomersByFiltersParams,
  TestBulkMessageParams,
  UpdateBulkMessagePayload,
  UploadBulkMessageWhatsappPhotoParams,
} from '../types/bulk-message.types';
import {
  normalizeBulkMessage,
  normalizeBulkMessageMultimedia,
  normalizeBulkMessageQueueStatus,
  normalizePageBulkMessageEvent,
} from '../utils/normalize-bulk-message';
import { buildBulkMessageInvalidationTags } from './bulk-message-api-tags';

export const bulkMessageApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBulkMessagesByBranch: builder.query<
      PaginatedResponse<BulkMessage>,
      GetBulkMessagesByBranchParams
    >({
      query: ({
        branchCode,
        page = 0,
        size = 20,
        search,
        sortBy = 'creationDate',
        sortDirection = 'desc',
      }) => {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('size', String(size));
        params.set('search', search?.trim() ?? '');
        params.set('sortBy', sortBy);
        params.set('sortDirection', sortDirection);
        return {
          url: `/bulkmessage/retrieve/${branchCode}?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response: Pro20ApiResponse<unknown>, _meta, arg) =>
        normalizePro20PageResponse(
          normalizePro20Response(response),
          normalizeBulkMessage,
          arg.size ?? 20,
        ),
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.BulkMessages, id: branchCode },
      ],
    }),

    getBulkMessageByCode: builder.query<BulkMessage, BulkMessageCodeParams>({
      query: ({ bulkMessageCode }) => ({
        url: `/bulkmessage/retrieve/bulkmmessage/${bulkMessageCode}`,
        method: 'GET',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) => {
        const data = normalizePro20Response(response);
        const normalized = normalizeBulkMessage(data);
        if (!normalized) {
          throw new Error('Invalid bulk message response');
        }
        return normalized;
      },
      providesTags: (_result, _error, { bulkMessageCode }) => [
        { type: API_TAGS.BulkMessages, id: bulkMessageCode },
      ],
    }),

    createBulkMessage: builder.mutation<BulkMessage, CreateBulkMessagePayload>({
      query: (body) => ({
        url: '/bulkmessage/create',
        method: 'POST',
        body,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) => {
        const data = normalizePro20Response(response);
        const normalized = normalizeBulkMessage(data);
        if (!normalized) {
          throw new Error('Invalid create bulk message response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.BulkMessages, id: branchCode },
      ],
    }),

    updateBulkMessage: builder.mutation<BulkMessage, UpdateBulkMessagePayload>({
      query: (body) => ({
        url: '/bulkmessage/update',
        method: 'PUT',
        body,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) => {
        const data = normalizePro20Response(response);
        const normalized = normalizeBulkMessage(data);
        if (!normalized) {
          throw new Error('Invalid update bulk message response');
        }
        return normalized;
      },
      invalidatesTags: (result) =>
        result
          ? buildBulkMessageInvalidationTags(
              result.bulkMessageCode,
              result.branchCode,
            )
          : [{ type: API_TAGS.BulkMessages, id: 'LIST' }],
    }),

    deleteBulkMessage: builder.mutation<null, BulkMessageCodeParams>({
      query: ({ bulkMessageCode }) => ({
        url: `/bulkmessage/delete/${bulkMessageCode}`,
        method: 'DELETE',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown> | null) => {
        if (response) {
          normalizePro20Response(response);
        }
        return null;
      },
      invalidatesTags: (_result, _error, { branchCode }) =>
        branchCode ? [{ type: API_TAGS.BulkMessages, id: branchCode }] : [],
    }),

    duplicateBulkMessage: builder.mutation<BulkMessage, BulkMessageCodeParams>({
      query: ({ bulkMessageCode }) => ({
        url: `/bulkmessage/duplicate/${bulkMessageCode}`,
        method: 'PUT',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) => {
        const data = normalizePro20Response(response);
        const normalized = normalizeBulkMessage(data);
        if (!normalized) {
          throw new Error('Invalid duplicate bulk message response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { branchCode }) =>
        branchCode ? [{ type: API_TAGS.BulkMessages, id: branchCode }] : [],
    }),

    startBulkMessage: builder.mutation<string, BulkMessageCodeParams>({
      query: ({ bulkMessageCode }) => ({
        url: `/bulkmessage/start/${bulkMessageCode}`,
        method: 'PUT',
      }),
      transformResponse: (response: unknown) => {
        if (typeof response === 'string') {
          return response;
        }
        const data = normalizePro20Response(
          response as Pro20ApiResponse<unknown>,
        );
        return typeof data === 'string' ? data : '';
      },
      invalidatesTags: (_result, _error, { bulkMessageCode, branchCode }) =>
        buildBulkMessageInvalidationTags(bulkMessageCode, branchCode),
    }),

    stopBulkMessage: builder.mutation<string, BulkMessageCodeParams>({
      query: ({ bulkMessageCode }) => ({
        url: `/bulkmessage/stop/${bulkMessageCode}`,
        method: 'PUT',
      }),
      transformResponse: (response: unknown) => {
        if (typeof response === 'string') {
          return response;
        }
        const data = normalizePro20Response(
          response as Pro20ApiResponse<unknown>,
        );
        return typeof data === 'string' ? data : '';
      },
      invalidatesTags: (_result, _error, { bulkMessageCode, branchCode }) =>
        buildBulkMessageInvalidationTags(bulkMessageCode, branchCode),
    }),

    restartBulkMessage: builder.mutation<string, BulkMessageCodeParams>({
      query: ({ bulkMessageCode }) => ({
        url: `/bulkmessage/restart/${bulkMessageCode}`,
        method: 'PUT',
      }),
      transformResponse: (response: unknown) => {
        if (typeof response === 'string') {
          return response;
        }
        const data = normalizePro20Response(
          response as Pro20ApiResponse<unknown>,
        );
        return typeof data === 'string' ? data : '';
      },
      invalidatesTags: (_result, _error, { bulkMessageCode, branchCode }) =>
        buildBulkMessageInvalidationTags(bulkMessageCode, branchCode),
    }),

    addBulkMessageCustomers: builder.mutation<
      BulkMessage,
      AddBulkMessageCustomersParams
    >({
      query: ({ bulkMessageCode, customerIdsList }) => {
        const params = new URLSearchParams();
        customerIdsList.forEach((customerId) => {
          params.append('customerIdsList', String(customerId));
        });

        return {
          url: `/bulkmessage/update/${encodeURIComponent(bulkMessageCode)}/addcustomeridslist?${params.toString()}`,
          method: 'PUT',
        };
      },
      transformResponse: (response: Pro20ApiResponse<unknown>) => {
        const data = normalizePro20Response(response);
        const normalized = normalizeBulkMessage(data);
        if (!normalized) {
          throw new Error('Invalid add customers response');
        }
        return normalized;
      },
      invalidatesTags: (
        _result,
        _error,
        { bulkMessageCode, branchCode, skipInvalidation },
      ) =>
        skipInvalidation
          ? []
          : buildBulkMessageInvalidationTags(bulkMessageCode, branchCode),
    }),

    selectBulkMessageCustomersByFilters: builder.mutation<
      BulkMessage,
      SelectBulkMessageCustomersByFiltersParams
    >({
      query: ({
        bulkMessageCode,
        exclude,
        marketingStatus,
        search,
        tags,
        months,
        yearsOfBirth,
        numbersOfBookings,
        includeMail,
        includeCustomersWithMarketingConsent,
        isInBlackList,
        minEdgeBookingPeriod,
        maxEdgeBookingPeriod,
        prefix,
        formCode,
      }) => {
        const params = new URLSearchParams();
        appendCustomerExclusions(params, exclude);
        if (marketingStatus) params.set('marketingStatus', marketingStatus);
        params.set('useListingFilters', 'true');
        params.set('search', search.trim());

        tags.forEach((tag) => {
          const value = tag.trim();
          if (value) {
            params.append('tags[]', value);
          }
        });
        months.forEach((month) => {
          const value = month.trim();
          if (value) {
            params.append('month[]', value);
          }
        });
        yearsOfBirth.forEach((year) => {
          const value = year.trim();
          if (value) {
            params.append('yearsOfBirth[]', value);
          }
        });

        params.set('numbersOfBookings', numbersOfBookings);
        params.set('includeMail', String(includeMail));
        params.set(
          'includeCustomersWithMarketingConsent',
          String(includeCustomersWithMarketingConsent),
        );
        params.set('isInBlackList', String(isInBlackList));
        params.set('minEdgeBookingPeriod', minEdgeBookingPeriod);
        params.set('maxEdgeBookingPeriod', maxEdgeBookingPeriod);
        params.set('prefix', prefix);
        params.set('formCode', formCode);

        return {
          url: `/bulkmessage/update/${encodeURIComponent(bulkMessageCode)}/addcustomeridslist?${params.toString()}`,
          method: 'PUT',
        };
      },
      transformResponse: (response: Pro20ApiResponse<unknown>) => {
        const data = normalizePro20Response(response);
        const normalized = normalizeBulkMessage(data);
        if (!normalized) {
          throw new Error('Invalid select customers response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { bulkMessageCode, branchCode }) =>
        buildBulkMessageInvalidationTags(bulkMessageCode, branchCode),
    }),

    clearBulkMessageCustomers: builder.mutation<
      BulkMessage,
      BulkMessageCodeParams
    >({
      query: ({ bulkMessageCode }) => ({
        url: `/bulkmessage/update/${bulkMessageCode}/clercustomeridslist`,
        method: 'DELETE',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) => {
        const data = normalizePro20Response(response);
        const normalized = normalizeBulkMessage(data);
        if (!normalized) {
          throw new Error('Invalid clear customers response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { bulkMessageCode, branchCode }) =>
        buildBulkMessageInvalidationTags(bulkMessageCode, branchCode),
    }),

    uploadBulkMessageWhatsappPhoto: builder.mutation<
      BulkMessageMultimedia,
      UploadBulkMessageWhatsappPhotoParams
    >({
      query: ({ bulkMessageCode, formData }) => ({
        url: `/bulkmessage/upload/whatsappPhoto/${bulkMessageCode}`,
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        normalizeBulkMessageMultimedia(normalizePro20Response(response)),
      invalidatesTags: (_result, _error, { bulkMessageCode, branchCode }) =>
        buildBulkMessageInvalidationTags(bulkMessageCode, branchCode),
    }),

    removeBulkMessageWhatsappPhoto: builder.mutation<
      null,
      BulkMessageCodeParams
    >({
      query: ({ bulkMessageCode }) => ({
        url: `/bulkmessage/remove/logophoto/${bulkMessageCode}`,
        method: 'DELETE',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown> | null) => {
        if (response) {
          normalizePro20Response(response);
        }
        return null;
      },
      invalidatesTags: (_result, _error, { bulkMessageCode, branchCode }) =>
        buildBulkMessageInvalidationTags(bulkMessageCode, branchCode),
    }),

    testBulkMessage: builder.mutation<null, TestBulkMessageParams>({
      query: ({ bulkMessageCode, phone }) => ({
        url: `/bulkmessage/test/${bulkMessageCode}/${encodeURIComponent(phone)}`,
        method: 'GET',
      }),
      transformResponse: () => null,
    }),

    getBulkMessageStatus: builder.query<
      BulkMessageQueueStatusDto,
      BulkMessageCodeParams
    >({
      query: ({ bulkMessageCode }) => ({
        url: `/bulkmessage/status/${bulkMessageCode}`,
        method: 'GET',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        normalizeBulkMessageQueueStatus(normalizePro20Response(response)),
      providesTags: (_result, _error, { bulkMessageCode }) => [
        { type: API_TAGS.BulkMessages, id: `${bulkMessageCode}-status` },
      ],
    }),

    getBulkMessageEvents: builder.query<
      PageBulkMessageEvent,
      RetrieveBulkMessageEventsParams
    >({
      query: ({
        bulkMessageCode,
        page,
        size,
        completed,
        failed,
        pending,
        query,
      }) => ({
        url: `/bulkmessage/retrievebulkdetails/${bulkMessageCode}/events`,
        method: 'GET',
        params: {
          page: page ?? 0,
          size: size ?? 100,
          completed: completed || undefined,
          failed: failed || undefined,
          pending: pending || undefined,
          query: query || undefined,
        },
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        normalizePageBulkMessageEvent(normalizePro20Response(response)),
      providesTags: (_result, _error, { bulkMessageCode }) => [
        { type: API_TAGS.BulkMessages, id: `${bulkMessageCode}-events` },
      ],
    }),
  }),
});

export const {
  useGetBulkMessagesByBranchQuery,
  useGetBulkMessageByCodeQuery,
  useCreateBulkMessageMutation,
  useUpdateBulkMessageMutation,
  useDeleteBulkMessageMutation,
  useDuplicateBulkMessageMutation,
  useStartBulkMessageMutation,
  useStopBulkMessageMutation,
  useRestartBulkMessageMutation,
  useAddBulkMessageCustomersMutation,
  useSelectBulkMessageCustomersByFiltersMutation,
  useClearBulkMessageCustomersMutation,
  useUploadBulkMessageWhatsappPhotoMutation,
  useRemoveBulkMessageWhatsappPhotoMutation,
  useTestBulkMessageMutation,
  useGetBulkMessageStatusQuery,
  useLazyGetBulkMessageStatusQuery,
  useGetBulkMessageEventsQuery,
} = bulkMessageApi;
